/**
 * Registro de handlers do botão "voltar" do Android (LIFO).
 *
 * Antes o App.tsx só olhava o `phase` global: qualquer submenu (loja, config
 * do Battle Royale, lobby...) mantém `phase === "menu"`, então o back FECHAVA
 * O APP de dentro de qualquer tela interna. Agora componentes com navegação
 * própria registram um handler; o mais recente (topo da pilha de UI) decide
 * primeiro. Handler retorna `true` para consumir o evento.
 */
import { useEffect, useRef } from "react";

type BackHandler = () => boolean;

const handlers: BackHandler[] = [];

export function registerBackHandler(handler: BackHandler): () => void {
  handlers.push(handler);
  return () => {
    const index = handlers.indexOf(handler);
    if (index >= 0) handlers.splice(index, 1);
  };
}

/** Percorre os handlers do mais recente ao mais antigo; true = consumido. */
export function dispatchBack(): boolean {
  for (let i = handlers.length - 1; i >= 0; i--) {
    try {
      if (handlers[i]()) return true;
    } catch (error) {
      console.warn("[backButton] Handler error:", error);
    }
  }
  return false;
}

/** Hook: registra um handler enquanto `enabled` (sempre com a closure atual). */
export function useBackHandler(handler: BackHandler, enabled: boolean = true) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    return registerBackHandler(() => handlerRef.current());
  }, [enabled]);
}
